// What OnlineStateService does when the couch login it runs on coming back online returns false.
//
// The login can fail for very different reasons: the user signed out meanwhile, the server is
// behind this account's data version, the network dropped, or the server genuinely refused us.
// Only the last one is an authentication failure worth an error report and a user-facing message;
// the others are expected and recover on the next retry. The decision lives here, apart from the
// service, so it can be tested under node.
//
// Pure module (no Angular deps) for node unit tests — same pattern as couch-login-online-gate.ts.

import { CouchLoginSkipReason } from './couch-login-error-classify';

export enum ComingOnlineLoginFailure {
  // the user signed out (or Firebase dropped the session) while the login ran: log only
  SignOutRace = 'SignOutRace',
  // #4283: the server runs an older _users format than this account's data: log only
  VersionMismatch = 'VersionMismatch',
  // the login failed at the network layer: silent problem, no user message
  NetworkFailure = 'NetworkFailure',
  // #6231: Firebase had not restored the user, so the login never asked the server: silent
  // problem, no user message
  FirebaseNotReady = 'FirebaseNotReady',
  // df-97e69e66c032: every retry hit the iOS system timeout; the retry loop already reported it
  // as "too many retries", so log only, no second report and no user message
  LoginTimedOut = 'LoginTimedOut',
  // nothing above explains it: error report plus the user-facing auth-failed message
  AuthFailure = 'AuthFailure',
}

export interface ComingOnlineLoginFailureState {
  /** `AuthService.isLikelySignOutRace()`, read after the re-probe await */
  signOutRace: boolean;
  /** the stored login error is the server-behind-account-version refusal */
  versionMismatch: boolean;
  /** the stored login error is an expected, recoverable (network-shaped) one */
  recoverableError: boolean;
  /** df-97e69e66c032: the stored login error is the iOS system timeout ("The request timed out.") */
  timedOut: boolean;
  /** #6231: why the login ended without calling the server, or null when it did call it */
  skipReason: CouchLoginSkipReason | null;
}

/**
 * Pick the outcome for a failed coming-online couch login. The order matters: a sign-out race
 * explains any failure, so it is checked first.
 */
export function decideComingOnlineLoginFailure(state: ComingOnlineLoginFailureState): ComingOnlineLoginFailure {
  if (state.signOutRace) return ComingOnlineLoginFailure.SignOutRace;
  if (state.versionMismatch) return ComingOnlineLoginFailure.VersionMismatch;
  if (state.recoverableError) return ComingOnlineLoginFailure.NetworkFailure;
  // df-97e69e66c032: the phone gave up waiting on every retry. Nothing refused us, so this is not
  // an authentication failure; the shared classifier keeps it non-recoverable only so a slow
  // server stays reported, which the retry loop already did.
  if (state.timedOut) return ComingOnlineLoginFailure.LoginTimedOut;
  // #6231: the login never asked the server, so nothing refused us. OfflineAtStart means its own
  // reachability re-probe failed, which is a network failure. NoFirebaseUser that is not a
  // sign-out race means Firebase restored the user late (a rejected session would still have no
  // user and is caught as a sign-out race above); the slow restore is reported on its own as
  // BootAuthStateReadyTimeout. AuthTokenUnavailable falls through: Firebase has a user but
  // could not vouch for it, which may be a real sign-in problem.
  if (state.skipReason === CouchLoginSkipReason.OfflineAtStart) return ComingOnlineLoginFailure.NetworkFailure;
  if (state.skipReason === CouchLoginSkipReason.NoFirebaseUser) return ComingOnlineLoginFailure.FirebaseNotReady;
  return ComingOnlineLoginFailure.AuthFailure;
}
